/**
 * Settings > Technician schedules (tracker item 40): per-technician working
 * hours (or the company default) and time off. Office roles only, like the
 * backend /dispatch routes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createAPIClient, IDispatchApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, rowDangerActionClass, secondaryButtonClass } from '../../components/formStyles';
import { getErrorMessage } from '../../utils/apiError';
import { formatJobDate } from '../../utils/jobPresentation';
import { DAY_SHORT, formatTime } from '../dispatchUi';
import { Toast } from '../../components/Toast';

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function WorkDaysPicker({ value, onChange, disabled, idPrefix }: {
  value: number[];
  onChange: (days: number[]) => void;
  disabled?: boolean;
  idPrefix: string;
}) {
  return (
    <fieldset className="flex flex-wrap gap-3" disabled={disabled}>
      <legend className="sr-only">Working days</legend>
      {DAY_ORDER.map((day) => (
        <label key={day} htmlFor={`${idPrefix}-${day}`} className="inline-flex items-center gap-1.5 text-sm text-gray-700">
          <input id={`${idPrefix}-${day}`} type="checkbox" checked={value.includes(day)}
            onChange={(event) => onChange(event.target.checked ? [...value, day].sort((a, b) => a - b) : value.filter((d) => d !== day))} />
          {DAY_SHORT[day]}
        </label>
      ))}
    </fieldset>
  );
}

export function TechnicianSchedulesPage() {
  const api = useMemo(() => createAPIClient() as IDispatchApi & IUsersApi, []);
  const [people, setPeople] = useState<types.AssignableUser[]>([]);
  const [userId, setUserId] = useState('');
  const [hours, setHours] = useState<types.TechnicianWorkHours | null>(null);
  const [useDefault, setUseDefault] = useState(true);
  const [days, setDays] = useState<number[]>([]);
  const [start, setStart] = useState('08:00');
  const [end, setEnd] = useState('17:00');
  const [timeOff, setTimeOff] = useState<types.TimeOff[]>([]);
  const [offStart, setOffStart] = useState('');
  const [offEnd, setOffEnd] = useState('');
  const [offAllDay, setOffAllDay] = useState(true);
  const [offFrom, setOffFrom] = useState('09:00');
  const [offTo, setOffTo] = useState('12:00');
  const [offReason, setOffReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    api.listAssignableUsers()
      .then((rows) => {
        setPeople(rows);
        const first = rows.find((r) => r.role === types.UserRole.TECHNICIAN) ?? rows[0];
        if (first) setUserId(first.id);
      })
      .catch((err) => setError(getErrorMessage(err, 'Failed to load technicians')));
  }, [api]);

  const loadPerson = useCallback(async (id: string) => {
    if (!id) return;
    setError(null);
    try {
      const [h, off] = await Promise.all([api.getTechnicianWorkHours(id), api.listTimeOff({ userId: id })]);
      setHours(h);
      setUseDefault(h.source === 'COMPANY');
      setDays(h.workDays);
      setStart(h.startTime);
      setEnd(h.endTime);
      setTimeOff(off);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load the schedule'));
    }
  }, [api]);

  useEffect(() => { loadPerson(userId); }, [loadPerson, userId]);

  const saveHours = async () => {
    if (!userId) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      if (useDefault) {
        setHours(await api.clearTechnicianWorkHours(userId));
        setNotice('Using the company working hours.');
      } else {
        if (days.length === 0) { setError('Pick at least one working day.'); return; }
        if (end <= start) { setError('Working hours must end after they start.'); return; }
        setHours(await api.setTechnicianWorkHours(userId, { workDays: days, startTime: start, endTime: end }));
        setNotice('Working hours saved.');
      }
      await loadPerson(userId);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save working hours'));
    } finally {
      setBusy(false);
    }
  };

  const addTimeOff = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!userId || !offStart) { setError('Pick a start date.'); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      await api.createTimeOff({
        userId,
        startDate: offStart,
        endDate: offEnd || offStart,
        startTime: offAllDay ? null : offFrom,
        endTime: offAllDay ? null : offTo,
        reason: offReason.trim() || null,
      });
      setOffStart(''); setOffEnd(''); setOffReason(''); setOffAllDay(true);
      setNotice('Time off added.');
      setTimeOff(await api.listTimeOff({ userId }));
    } catch (err) {
      setError(getErrorMessage(err, 'Could not add time off'));
    } finally {
      setBusy(false);
    }
  };

  const removeTimeOff = async (entry: types.TimeOff) => {
    if (!window.confirm(`Remove time off starting ${formatJobDate(entry.startDate)}?`)) return;
    setBusy(true); setError(null);
    try {
      await api.deleteTimeOff(entry.id);
      setTimeOff((rows) => rows.filter((r) => r.id !== entry.id));
      setNotice('Time off removed.');
    } catch (err) {
      setError(getErrorMessage(err, 'Could not remove time off'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
      <h1 className="mb-2 text-2xl font-bold text-gray-900">Technician schedules</h1>
      <p className="mb-6 text-sm text-gray-600">Working hours and time off used by the dispatch board's warnings. They never block scheduling.</p>
      {notice && <Toast kind="success">{notice}</Toast>}
      {error && <Toast kind="error">{error}</Toast>}

      <div className="mb-6">
        <label htmlFor="schedule-person" className={labelClass}>Technician</label>
        <select id="schedule-person" className={`${inputClass} sm:max-w-md`} value={userId} onChange={(event) => setUserId(event.target.value)}>
          {people.map((p) => <option key={p.id} value={p.id}>{p.email} ({p.role === types.UserRole.TECHNICIAN ? 'Technician' : p.role === types.UserRole.ADMIN ? 'Admin' : 'Office manager'})</option>)}
        </select>
      </div>

      {hours && (
        <section className="mb-6 rounded-lg bg-white p-5 shadow" aria-label="Working hours">
          <h2 className="mb-3 text-lg font-semibold">Working hours</h2>
          <label className="mb-4 inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={useDefault} disabled={busy} onChange={(event) => setUseDefault(event.target.checked)} />
            Use the company default ({hours.companyDefault.workDays.map((d) => DAY_SHORT[d]).join(', ')}, {formatTime(hours.companyDefault.startTime)} - {formatTime(hours.companyDefault.endTime)})
          </label>
          {!useDefault && (
            <div className="space-y-4">
              <WorkDaysPicker idPrefix="tech-day" value={days} onChange={setDays} disabled={busy} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div><label htmlFor="tech-start" className={labelClass}>Start</label><input id="tech-start" type="time" className={inputClass} value={start} disabled={busy} onChange={(event) => setStart(event.target.value)} /></div>
                <div><label htmlFor="tech-end" className={labelClass}>End</label><input id="tech-end" type="time" className={inputClass} value={end} disabled={busy} onChange={(event) => setEnd(event.target.value)} /></div>
              </div>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={saveHours}>Save working hours</button>
          </div>
        </section>
      )}

      {userId && (
        <section className="rounded-lg bg-white p-5 shadow" aria-label="Time off">
          <h2 className="mb-3 text-lg font-semibold">Time off</h2>
          {timeOff.length === 0 ? <p className="text-sm text-gray-600">No upcoming time off.</p> : (
            <ul className="mb-4 divide-y divide-gray-100">
              {timeOff.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="break-words">
                    {formatJobDate(entry.startDate)}{entry.endDate !== entry.startDate ? ` to ${formatJobDate(entry.endDate)}` : ''}
                    {entry.startTime ? `, ${formatTime(entry.startTime)} - ${formatTime(entry.endTime)}` : ', all day'}
                    {entry.reason ? ` · ${entry.reason}` : ''}
                  </span>
                  <button type="button" className={rowDangerActionClass} disabled={busy} onClick={() => removeTimeOff(entry)}>Remove</button>
                </li>
              ))}
            </ul>
          )}
          <form className="grid grid-cols-1 gap-4 border-t pt-4 sm:grid-cols-2" onSubmit={addTimeOff} aria-label="Add time off">
            <div><label htmlFor="off-start" className={labelClass}>From</label><input id="off-start" type="date" className={inputClass} value={offStart} disabled={busy} onChange={(event) => setOffStart(event.target.value)} /></div>
            <div><label htmlFor="off-end" className={labelClass}>To (optional)</label><input id="off-end" type="date" className={inputClass} value={offEnd} disabled={busy} onChange={(event) => setOffEnd(event.target.value)} /></div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
              <input type="checkbox" checked={offAllDay} disabled={busy} onChange={(event) => setOffAllDay(event.target.checked)} /> All day
            </label>
            {!offAllDay && (
              <>
                <div><label htmlFor="off-from" className={labelClass}>Start time</label><input id="off-from" type="time" className={inputClass} value={offFrom} disabled={busy} onChange={(event) => setOffFrom(event.target.value)} /></div>
                <div><label htmlFor="off-to" className={labelClass}>End time</label><input id="off-to" type="time" className={inputClass} value={offTo} disabled={busy} onChange={(event) => setOffTo(event.target.value)} /></div>
              </>
            )}
            <div className="sm:col-span-2"><label htmlFor="off-reason" className={labelClass}>Reason (optional)</label><input id="off-reason" className={inputClass} maxLength={200} value={offReason} disabled={busy} onChange={(event) => setOffReason(event.target.value)} /></div>
            <div className="flex justify-end sm:col-span-2"><button type="submit" className={secondaryButtonClass} disabled={busy}>Add time off</button></div>
          </form>
        </section>
      )}
    </main>
  );
}
